import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRooms, fetchSngRooms, fetchTournaments, registerTournament, unregisterTournament } from "../redux/roomSlice";
import { quickSeat, returnToGame } from "../redux/gameSlice";
import GameRoom from "./inGameLayout/GameRoom";
import "./roomslist.css";

const RoomsList = () => {
    const dispatch = useDispatch();
    const { list, sngList, tournamentList, loading, error } = useSelector((state) => state.rooms);
    const {
      activeGameScreen,
      primaryGameId,
      secondaryGameId,
      mySeats,
      error: joinError,
    } = useSelector((state) => state.games);

    // Which lobby tab is showing: 'holdem' (the cash tables, default) or 'sng'
    // (Sit-and-Go tournaments — fixed buy-in, start when full, payouts by place).
    const [tab, setTab] = useState("holdem");

    // The player is sitting at a table if they hold a seat in any open game. While seated
    // they can hit "+ Game" to browse the rooms list; this lets them jump straight back to
    // their game(s) without having to find and click their specific room.
    const isSeated = Object.values(mySeats).some((seat) => seat != null && seat >= 0);

    // Fetch the visible tab's list (re-fetch on each switch so seat counts are fresh).
    useEffect(() => {
      if (tab === "sng") dispatch(fetchSngRooms());
      else if (tab === "tournament") dispatch(fetchTournaments());
      else dispatch(fetchRooms());
    }, [dispatch, tab]);

    // Register / unregister for a scheduled or late-reg tournament (buy-in debited on register).
    const handleTournamentClick = (room) => {
      const now = Date.now();
      const registrationOpen = room.status === "SCHEDULED"
        || (room.status === "RUNNING" && room.lateRegClosesAt && now <= room.lateRegClosesAt);
      if (room.registered) return; // manage via the row's Unregister button
      if (registrationOpen) dispatch(registerTournament(room.id));
    };

    const formatStart = (epochMs) => {
      if (!epochMs) return "—";
      const d = new Date(epochMs);
      return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    };

    const handleJoin = (parentRoomId) => {
      // Both split-screen slots are already full — can't open a third table; go back.
      if (primaryGameId != null && secondaryGameId != null) {
        dispatch(returnToGame());
        return;
      }
      // Otherwise auto-seat: the backend picks the table + open seat (creating an overflow
      // table if every table this player isn't already on is full). Opens primary if it's
      // the first table, else split-screen secondary. (Use the "Return to Game" button to
      // jump back to an existing table without taking a new seat.) Sit-and-Go rooms never
      // overflow — the backend rejects the click when the tournament is full or running.
      dispatch(quickSeat({ parentRoomId }));
    };

    return (
      <>
        {!activeGameScreen && (
          <div className="rooms-list">
            {loading && <p>Loading...</p>}
            {error && <p style={{ color: "#ff3d6e" }}>{error}</p>}
            {typeof joinError === "string" && joinError && (
              <p style={{ color: "#ff3d6e" }}>{joinError}</p>
            )}
            <div className="rooms-table-wrap">
            <h2 className="rooms-table-header">Tables</h2>
            <div className="rooms-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === "holdem"}
                className={`rooms-tab ${tab === "holdem" ? "active" : ""}`}
                onClick={() => setTab("holdem")}
              >
                Hold'em
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "sng"}
                className={`rooms-tab ${tab === "sng" ? "active" : ""}`}
                onClick={() => setTab("sng")}
              >
                Sit-and-Go
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "tournament"}
                className={`rooms-tab ${tab === "tournament" ? "active" : ""}`}
                onClick={() => setTab("tournament")}
              >
                Tournaments
              </button>
            </div>
            <div className="rooms-table-scroll">
            {tab === "holdem" ? (
            <table>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Players</th>
                        <th>Table Size</th>
                        <th>Blinds</th>
                        <th>Buyins</th>
                    </tr>
                </thead>
                <tbody>
                    {list.map((room) =>(
                        <tr key={room.id} onClick={() => handleJoin(room.id)}>
                            <td>{room.name}</td>
                            <td>{room.numPlayers}</td>
                            <td>{room.maxPlayers}</td>
                            <td>{room.smallBlind}/{room.bigBlind}</td>
                            <td>{room.minBuyIn}/{room.maxBuyIn}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            ) : tab === "sng" ? (
            <table>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Players</th>
                        <th>Buy-in</th>
                        <th>Payouts</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {sngList.map((room) => (
                        <tr
                            key={room.id}
                            className={room.started ? "sng-row-running" : ""}
                            onClick={() => !room.started && handleJoin(room.id)}
                        >
                            <td>{room.name}</td>
                            <td>{room.numPlayers}/{room.maxPlayers}</td>
                            <td>{room.buyIn}</td>
                            <td>{(room.payouts || "").split(",").join(" / ")}</td>
                            <td>{room.started ? "Running" : "Registering"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            ) : (
            <table>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Starts</th>
                        <th>Buy-in</th>
                        <th>Players</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {tournamentList.map((room) => {
                        const now = Date.now();
                        const regOpen = room.status === "SCHEDULED"
                            || (room.status === "RUNNING" && room.lateRegClosesAt && now <= room.lateRegClosesAt);
                        const clickable = !room.registered && regOpen;
                        const statusText = room.status === "RUNNING"
                            ? (regOpen ? "Running (late reg)" : "Running")
                            : "Registering";
                        return (
                            <tr
                                key={room.id}
                                className={clickable ? "" : "sng-row-running"}
                                onClick={() => clickable && handleTournamentClick(room)}
                                title={clickable ? `Register for ${room.buyIn} chips` : ""}
                            >
                                <td>
                                    {room.name}
                                    {room.registered && (
                                        <span className="tournament-registered"> — Registered ✓</span>
                                    )}
                                </td>
                                <td>{formatStart(room.scheduledStartAt)}</td>
                                <td>{room.buyIn}</td>
                                <td>{room.numRegistered}{room.numTables > 0 ? ` · ${room.numTables} tables` : ""}</td>
                                <td>
                                    {room.registered && room.status === "SCHEDULED" ? (
                                        <button
                                            type="button"
                                            className="tournament-unregister-btn"
                                            onClick={(e) => { e.stopPropagation(); dispatch(unregisterTournament(room.id)); }}
                                        >
                                            Unregister
                                        </button>
                                    ) : (
                                        statusText
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            )}
            </div>
            </div>
            {isSeated && (
              <button
                className="return-to-game-btn"
                onClick={() => dispatch(returnToGame())}
              >
                Return to Game
              </button>
            )}
          </div>
        )}

        {activeGameScreen && <GameRoom />}
      </>
    );
  };

  export default RoomsList;
